import { useEffect, useMemo, useRef, useState } from 'react';
import { DemTypeahead } from './DemTypeahead';
import { StandardSalesLinesPanel } from './StandardSalesLinesPanel';
import {
  deliveryDateWeekdayLabel,
  formatDemCustomerAddressDisplay,
  getDeliveryScheduleDisplay,
  isDeliveryDateAllowed,
} from '../../lib/demCustomers';
import { enrichReviewRowsWithCustItemBlocks } from '../../lib/demCustItemBlockList';
import { fetchItemDetails } from '../../lib/demSkuMap';
import {
  applySysPricesToRows,
  computeRowSysPrice,
  fetchPricesForItems,
  type DemPriceRow,
} from '../../lib/demPoPricing';
import type { DemPoItemOption, PoMappingContext } from '../../lib/demPoMapping';
import { reapplyItemsForCustomerChange } from '../../lib/demPoMapping';
import {
  fetchStandardSalesLines,
} from '../../lib/demStandardSalesLines';
import type { DemCustomer, DemPoExtractHeader, DemPoReviewRow } from '../../types/dem';

type PoReviewPanelProps = {
  header: DemPoExtractHeader;
  rows: DemPoReviewRow[];
  priceRows: DemPriceRow[];
  customers: DemCustomer[];
  itemOptions: DemPoItemOption[];
  mappingCtx: PoMappingContext;
  saving: boolean;
  onHeaderChange: (header: DemPoExtractHeader) => void;
  onRowsChange: (rows: DemPoReviewRow[]) => void;
  onPriceRowsChange: (rows: DemPriceRow[]) => void;
  onConfirm: () => void;
};

function parseFactor(value: string): number {
  const n = Number(String(value).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function formatFactor(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '1';
  return String(parseFloat(value.toPrecision(10)));
}

function isIgnoredCode(code: string): boolean {
  return ['IGNORE', 'SKIP', 'N/A', 'NONE'].includes(code.trim().toUpperCase());
}

function getGroupPdfQty(row: DemPoReviewRow, rows: DemPoReviewRow[]): number {
  if (row.isPrimary) return row.pdfQty;
  const primary = rows.find((candidate) => candidate.groupId === row.groupId && candidate.isPrimary);
  return primary?.pdfQty ?? row.pdfQty;
}

function insertAfterGroup(rows: DemPoReviewRow[], groupId: string, nextRow: DemPoReviewRow): DemPoReviewRow[] {
  let insertAt = -1;
  for (let i = 0; i < rows.length; i += 1) {
    if (rows[i]!.groupId === groupId) insertAt = i;
  }
  if (insertAt < 0) return [...rows, nextRow];
  const copy = [...rows];
  copy.splice(insertAt + 1, 0, nextRow);
  return copy;
}

export function PoReviewPanel({
  header,
  rows,
  priceRows,
  customers,
  itemOptions,
  mappingCtx,
  saving,
  onHeaderChange,
  onRowsChange,
  onPriceRowsChange,
  onConfirm,
}: PoReviewPanelProps) {
  const rowsRef = useRef(rows);
  const priceRowsRef = useRef(priceRows);
  const headerRef = useRef(header);
  const [rematching, setRematching] = useState(false);
  const [stdLines, setStdLines] = useState<Awaited<ReturnType<typeof fetchStandardSalesLines>>>([]);
  const [stdLoading, setStdLoading] = useState(false);
  const [stdOpen, setStdOpen] = useState(true);
  const rematchedCustomerRef = useRef(header.sys_customer_code ?? '');

  useEffect(() => {
    rematchedCustomerRef.current = header.sys_customer_code ?? '';
  }, [header.sys_customer_code]);
  rowsRef.current = rows;
  priceRowsRef.current = priceRows;
  headerRef.current = header;

  useEffect(() => {
    const code = header.sys_customer_code?.trim() ?? '';
    if (!code) {
      setStdLines([]);
      setStdLoading(false);
      return;
    }

    let cancelled = false;
    setStdLoading(true);
    void fetchStandardSalesLines(code)
      .then((lines) => {
        if (!cancelled) setStdLines(lines);
      })
      .catch(() => {
        if (!cancelled) setStdLines([]);
      })
      .finally(() => {
        if (!cancelled) setStdLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [header.sys_customer_code]);

  const customerOptions = useMemo(
    () =>
      customers.map((customer) => ({
        value: customer.code,
        label: `${customer.code} - ${customer.name}`,
        blocked: customer.is_blocked,
        customerName: customer.name,
        address: customer.address,
        address2: customer.address_2,
        postal: customer.postal || undefined,
      })),
    [customers],
  );

  const itemTypeaheadOptions = useMemo(
    () =>
      itemOptions.map((item) => ({
        value: item.code,
        label: `${item.code} - ${item.is_blocked ? '🚫 [BLOCKED] ' : ''}${item.description}`,
        blocked: item.is_blocked,
        description: item.description,
        inventory: item.inventory,
        uom: item.uom,
        packing: item.packing,
      })),
    [itemOptions],
  );

  const itemBasePriceMapRef = useRef<Record<string, number>>({});
  const focusedFieldRef = useRef<{ rowId: string; field: 'factor' | 'finalQty' } | null>(null);
  const itemLookupSeqRef = useRef<Record<string, number>>({});

  const commitHeader = (nextHeader: DemPoExtractHeader) => {
    onHeaderChange(nextHeader);
    onRowsChange(
      applySysPricesToRows(rowsRef.current, priceRowsRef.current, nextHeader, customers, itemBasePriceMapRef.current),
    );
  };

  const patchRows = (id: string, patch: Partial<DemPoReviewRow>) => {
    const nextRows = rowsRef.current.map((row) => {
      if (row.id !== id) return row;
      const next = { ...row, ...patch };
      const pdfQty = getGroupPdfQty(next, rowsRef.current);
      const focus = focusedFieldRef.current;
      const editingThisRow = focus?.rowId === id;

      if ('factor' in patch && !(editingThisRow && focus?.field === 'finalQty')) {
        next.finalQty = Math.round(pdfQty * parseFactor(next.factor));
      }
      if ('finalQty' in patch && !(editingThisRow && focus?.field === 'factor') && pdfQty > 0) {
        next.factor = formatFactor((Number(next.finalQty) || 0) / pdfQty);
      }
      next.sysPrice = computeRowSysPrice(
        priceRowsRef.current,
        next,
        headerRef.current,
        customers,
        itemBasePriceMapRef.current[next.sysCode.trim().toUpperCase()] ?? 0,
      );
      return next;
    });
    // Keep ref in sync immediately so blur→Confirm races and item lookup see the new code.
    rowsRef.current = nextRows;
    onRowsChange(nextRows);
  };

  const addSubRow = (primaryId: string) => {
    const primary = rowsRef.current.find((row) => row.id === primaryId && row.isPrimary);
    if (!primary) return;

    const subRow: DemPoReviewRow = {
      id: `${primary.groupId}-sub-${Date.now()}`,
      groupId: primary.groupId,
      isPrimary: false,
      pdfKey: primary.pdfKey,
      pdfDesc: primary.pdfDesc,
      pdfQty: primary.pdfQty,
      pdfUom: primary.pdfUom,
      pdfPrice: primary.pdfPrice,
      sysCode: '',
      factor: '1',
      sysDesc: '',
      sysUom: '',
      sysPacking: '',
      sysBlocked: false,
      sysPrice: '0.000',
      finalQty: 0,
    };

    onRowsChange(insertAfterGroup(rowsRef.current, primary.groupId, subRow));
  };

  const deleteSubRow = (id: string) => {
    const row = rowsRef.current.find((candidate) => candidate.id === id);
    if (!row || row.isPrimary) return;
    onRowsChange(rowsRef.current.filter((candidate) => candidate.id !== id));
  };

  const refreshItemDetails = async (id: string, rawCode: string) => {
    const code = rawCode.trim();
    const seq = (itemLookupSeqRef.current[id] ?? 0) + 1;
    itemLookupSeqRef.current[id] = seq;

    if (!code || isIgnoredCode(code)) {
      if (itemLookupSeqRef.current[id] !== seq) return;
      patchRows(id, {
        sysDesc: isIgnoredCode(code) ? '(ignored)' : '',
        sysUom: '',
        sysPacking: '',
        sysBlocked: false,
        sysPrice: '0.000',
      });
      return;
    }

    try {
      const [details, fetchedPrices] = await Promise.all([
        fetchItemDetails([code]),
        fetchPricesForItems([code]),
      ]);
      if (itemLookupSeqRef.current[id] !== seq) return;
      const current = rowsRef.current.find((r) => r.id === id);
      if (!current || current.sysCode.trim().toUpperCase() !== code.toUpperCase()) return;

      const detail = details[code] ?? details[code.toUpperCase()];
      if (detail) {
        itemBasePriceMapRef.current[code.toUpperCase()] = detail.base_price;
      }
      const mergedPrices = [
        ...priceRowsRef.current.filter((p) => p.item !== code.toUpperCase()),
        ...fetchedPrices,
      ];
      priceRowsRef.current = mergedPrices;
      onPriceRowsChange(mergedPrices);

      const patched: Partial<DemPoReviewRow> = {
        sysCode: code,
        sysDesc: detail?.description ?? '',
        sysUom: detail?.uom ?? '',
        sysPacking: detail?.packing ?? '',
        sysBlocked: detail?.is_blocked ?? false,
      };
      const next = { ...current, ...patched };
      patched.sysPrice = computeRowSysPrice(
        mergedPrices,
        next,
        headerRef.current,
        customers,
        detail?.base_price ?? 0,
      );

      const nextRows = rowsRef.current.map((r) => {
        if (r.id !== id) {
          return {
            ...r,
            sysPrice: computeRowSysPrice(
              mergedPrices,
              r,
              headerRef.current,
              customers,
              itemBasePriceMapRef.current[r.sysCode.trim().toUpperCase()] ?? 0,
            ),
          };
        }
        return { ...r, ...patched };
      });
      if (itemLookupSeqRef.current[id] !== seq) return;
      const customerCode = headerRef.current.sys_customer_code?.trim() ?? '';
      onRowsChange(
        customerCode
          ? await enrichReviewRowsWithCustItemBlocks(customerCode, nextRows)
          : nextRows,
      );
    } catch {
      // Keep current values if lookup fails.
    }
  };

  const handleSysCodeChange = (id: string, code: string) => {
    const normalized = isIgnoredCode(code) ? code.trim().toUpperCase() : code;
    patchRows(id, { sysCode: normalized });
  };

  const handleSysCodeCommit = (id: string, code: string) => {
    const normalized = isIgnoredCode(code) ? code.trim().toUpperCase() : code.trim();
    patchRows(id, { sysCode: normalized });
    void refreshItemDetails(id, normalized);
  };

  const handleCustomerCodeCommit = (code: string) => {
    const normalized = code.trim();
    if (!normalized || normalized === rematchedCustomerRef.current) return;
    rematchedCustomerRef.current = normalized;

    const customer = customers.find((c) => c.code === normalized);
    const nextHeader: DemPoExtractHeader = {
      ...header,
      sys_customer_code: normalized,
      sys_customer_name: customer?.name ?? '',
      sys_customer_address: customer ? formatDemCustomerAddressDisplay(customer) : '',
    };
    onHeaderChange(nextHeader);

    setRematching(true);
    void reapplyItemsForCustomerChange(mappingCtx, nextHeader, rowsRef.current, priceRowsRef.current)
      .then((result) => {
        onHeaderChange(result.header);
        onRowsChange(result.reviewRows);
        onPriceRowsChange(result.priceRows);
      })
      .finally(() => setRematching(false));
  };

  const selectedCustomer = customers.find((c) => c.code === header.sys_customer_code);
  const customerBlocked = selectedCustomer?.is_blocked ?? false;
  const scheduleDisplay = getDeliveryScheduleDisplay(selectedCustomer?.allowed_days);
  const deliveryDateIso = header.delivery_date.match(/^\d{4}-\d{2}-\d{2}$/) ? header.delivery_date : '';
  const deliveryDateOffSchedule =
    Boolean(deliveryDateIso && selectedCustomer) &&
    !isDeliveryDateAllowed(deliveryDateIso, selectedCustomer?.allowed_days);
  const deliveryDateTitle = deliveryDateOffSchedule
    ? `Warning: Customer [${header.sys_customer_code}] does not accept deliveries on ${deliveryDateWeekdayLabel(deliveryDateIso)}s.`
    : undefined;
  const deliveryWeekday = deliveryDateIso
    ? new Date(`${deliveryDateIso}T12:00:00`).toLocaleDateString('en-SG', { weekday: 'short' })
    : '';

  const primarySnById = useMemo(() => {
    const map = new Map<string, number>();
    let sn = 0;
    for (const row of rows) {
      if (!row.isPrimary) continue;
      sn += 1;
      map.set(row.id, sn);
    }
    return map;
  }, [rows]);

  const snForRow = (row: DemPoReviewRow): string => {
    if (row.isPrimary) {
      const sn = primarySnById.get(row.id);
      return sn != null ? String(sn) : '';
    }
    const primary = rows.find((r) => r.isPrimary && r.groupId === row.groupId);
    const sn = primary ? primarySnById.get(primary.id) : undefined;
    return sn != null ? String(sn) : '';
  };

  const invalidRows = rows.filter((row) => {
    const code = row.sysCode.trim().toUpperCase();
    const ignored = isIgnoredCode(code);
    return !ignored && row.finalQty > 0 && !row.sysCode.trim();
  });
  const custItemBlockedRows = rows.filter(
    (row) => row.finalQty > 0 && Boolean(row.sysCustItemBlockReason?.trim()),
  );

  return (
    <div className="dem-po-review">
      <div className={`dem-po-review__split${stdOpen ? '' : ' dem-po-review__split--collapsed'}`}>
        <div className="dem-po-review__main">
      <div className="dem-po-review__header-grid">
        <section className="dem-po-review__card dem-po-review__card--doc">
          <h3 className="dem-po-review__section-title">Document information</h3>
          <label>
            <span className="dem-po-review__label">Name</span>
            <input type="text" className="dem-po-review__field dem-po-review__field--readonly" value={header.company_name} readOnly />
          </label>
          <label>
            <span className="dem-po-review__label">Address</span>
            <input
              type="text"
              className="dem-po-review__field dem-po-review__field--readonly"
              value={header.company_address?.trim() ? header.company_address : '—'}
              readOnly
            />
          </label>
        </section>

        <section className="dem-po-review__card dem-po-review__card--sys">
          <h3 className="dem-po-review__section-title">System information</h3>
          <div className="dem-po-review__cust-row">
            <label className="dem-po-review__cust-code">
              <span className="dem-po-review__label">Customer no.</span>
              <DemTypeahead
                className="dem-po-review__typeahead"
                value={header.sys_customer_code ?? ''}
                onChange={(code) =>
                  onHeaderChange({
                    ...header,
                    sys_customer_code: code,
                  })
                }
                onCommit={handleCustomerCodeCommit}
                commitOnType={false}
                options={customerOptions}
                placeholder="Code / name"
                ariaLabel="Customer no."
                disabled={rematching}
                tableMode="customer"
              />
            </label>
            <label className="dem-po-review__cust-name">
              <span className="dem-po-review__label">Customer name</span>
              <input
                type="text"
                className={`dem-po-review__field dem-po-review__field--readonly${
                  customerBlocked ? ' dem-po-review__field--blocked' : ''
                }`}
                value={
                  customerBlocked
                    ? `🚫 [BLOCKED] ${header.sys_customer_name ?? ''}`
                    : header.sys_customer_name ?? ''
                }
                readOnly
              />
            </label>
          </div>
          <label>
            <span className="dem-po-review__label">Address</span>
            <input type="text" className="dem-po-review__field dem-po-review__field--readonly" value={header.sys_customer_address ?? ''} readOnly />
          </label>
        </section>
      </div>

      {rematching ? (
        <p className="dem-po-review__rematch-hint" role="status">
          Reloading SKU mappings from this customer&apos;s template…
        </p>
      ) : null}

      <div className="dem-po-review__order-meta">
        <label>
          <span className="dem-po-review__label">PO number</span>
          <input
            type="text"
            className="dem-po-review__field"
            value={header.po_number}
            onChange={(e) => onHeaderChange({ ...header, po_number: e.target.value })}
          />
        </label>
        <label className="dem-po-review__date-field">
          <span className="dem-po-review__label">Delivery date</span>
          <div className="dem-po-review__date-row">
            <input
              type="date"
              className={`dem-po-review__field${
                deliveryDateOffSchedule ? ' dem-po-review__field--warn' : ''
              }`}
              value={deliveryDateIso}
              title={deliveryDateTitle}
              onChange={(e) => commitHeader({ ...header, delivery_date: e.target.value })}
            />
            {deliveryWeekday ? (
              <span className="dem-po-review__date-weekday" aria-hidden>
                ({deliveryWeekday})
              </span>
            ) : null}
          </div>
          <span
            className={`dem-po-review__schedule dem-po-review__schedule--${scheduleDisplay.tone}`}
            role="status"
          >
            {scheduleDisplay.text}
          </span>
        </label>
      </div>

      <section className="dem-po-review__items">
        <h3 className="dem-po-review__section-title">System validation (items)</h3>
        <div className="dem-po-review__table-wrap">
          <div className="dem-po-review__table-head" aria-hidden>
            <span>S/N</span>
            <span>Item No.</span>
            <span>Description</span>
            <span>Qty</span>
            <span>UOM</span>
            <span>Packing</span>
            <span title="PO unit price (top) / NAV sales price (bottom)">Unit Price</span>
            <span>Factor</span>
            <span>Act</span>
          </div>
          {rows.map((row) => (
            <div
              key={row.id}
              className={`dem-po-review__item${row.isPrimary ? '' : ' dem-po-review__item--sub'}`}
            >
              {row.isPrimary ? (
                <div className="dem-po-review__table-row dem-po-review__table-row--po">
                  <span className="dem-po-review__cell dem-po-review__cell--sn dem-po-review__cell--muted">
                    {snForRow(row)}
                  </span>
                  <span className="dem-po-review__cell dem-po-review__cell--muted">
                    {row.pdfKey || '—'}
                  </span>
                  <span className="dem-po-review__cell dem-po-review__cell--muted">
                    {row.pdfDesc || '—'}
                  </span>
                  <span className="dem-po-review__cell dem-po-review__cell--muted">{row.pdfQty}</span>
                  <span className="dem-po-review__cell dem-po-review__cell--muted">
                    {row.pdfUom || '—'}
                  </span>
                  <span className="dem-po-review__cell dem-po-review__cell--muted">—</span>
                  <span className="dem-po-review__cell dem-po-review__cell--muted">
                    {row.pdfPrice || '—'}
                  </span>
                  <span className="dem-po-review__cell dem-po-review__cell--muted">—</span>
                  <div className="dem-po-review__act">
                    <button
                      type="button"
                      className="dem-po-review__act-btn dem-po-review__act-btn--add"
                      title="Add another system item for this PDF line"
                      onClick={() => addSubRow(row.id)}
                    >
                      +
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="dem-po-review__table-row dem-po-review__table-row--map">
                <span
                  className={`dem-po-review__cell dem-po-review__cell--sn${
                    row.isPrimary ? '' : ' dem-po-review__cell--muted'
                  }`}
                  aria-hidden={row.isPrimary}
                >
                  {row.isPrimary ? '' : snForRow(row)}
                </span>
                <DemTypeahead
                  className="dem-po-review__typeahead"
                  value={row.sysCode}
                  onChange={(code) => handleSysCodeChange(row.id, code)}
                  onCommit={(code) => handleSysCodeCommit(row.id, code)}
                  commitOnType={false}
                  options={itemTypeaheadOptions}
                  placeholder="Sys item / IGNORE"
                  ariaLabel={`System item code for ${row.pdfKey || row.pdfDesc}`}
                  tableMode
                />
                <span
                  className={`dem-po-review__cell dem-po-review__cell--sys-desc${
                    row.sysCustItemBlockReason?.trim()
                      ? ' dem-po-review__cell--cust-block'
                      : row.sysBlocked
                        ? ' dem-po-review__cell--blocked'
                        : ''
                  }`}
                  title={
                    row.sysCustItemBlockReason?.trim()
                      ? row.sysCustItemBlockReason
                      : row.sysBlocked
                        ? 'This item is blocked in the system.'
                        : undefined
                  }
                >
                  {row.sysCustItemBlockReason?.trim()
                    ? `⚠ ${row.sysCustItemBlockReason}`
                    : row.sysBlocked
                      ? `🚫 ${row.sysDesc || '—'}`
                      : row.sysDesc || '—'}
                </span>
                <input
                  className="dem-po-review__field dem-po-review__field--compact"
                  type="number"
                  min={0}
                  value={row.finalQty}
                  title="Final qty"
                  aria-label={`Final qty for ${row.pdfKey || row.pdfDesc}`}
                  onFocus={() => {
                    focusedFieldRef.current = { rowId: row.id, field: 'finalQty' };
                  }}
                  onBlur={() => {
                    if (
                      focusedFieldRef.current?.rowId === row.id &&
                      focusedFieldRef.current.field === 'finalQty'
                    ) {
                      focusedFieldRef.current = null;
                    }
                  }}
                  onChange={(e) => patchRows(row.id, { finalQty: Number(e.target.value) || 0 })}
                />
                <span className="dem-po-review__cell">{row.sysUom || '—'}</span>
                <span className="dem-po-review__cell dem-po-review__cell--packing">
                  {row.sysPacking || '—'}
                </span>
                <span
                  className="dem-po-review__cell dem-po-review__cell--price"
                  title="Navision sales price for this customer, UOM, qty and delivery date"
                >
                  {row.sysPrice || '0.000'}
                </span>
                <input
                  className="dem-po-review__field dem-po-review__field--compact"
                  type="text"
                  value={row.factor}
                  title="Factor"
                  aria-label={`Factor for ${row.pdfKey || row.pdfDesc}`}
                  onFocus={() => {
                    focusedFieldRef.current = { rowId: row.id, field: 'factor' };
                  }}
                  onBlur={() => {
                    if (
                      focusedFieldRef.current?.rowId === row.id &&
                      focusedFieldRef.current.field === 'factor'
                    ) {
                      focusedFieldRef.current = null;
                    }
                  }}
                  onChange={(e) => patchRows(row.id, { factor: e.target.value })}
                />
                <div className="dem-po-review__act">
                  {row.isPrimary ? (
                    <span className="dem-po-review__act-spacer" aria-hidden />
                  ) : (
                    <button
                      type="button"
                      className="dem-po-review__act-btn dem-po-review__act-btn--remove"
                      title="Remove this split line"
                      onClick={() => deleteSubRow(row.id)}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {custItemBlockedRows.length > 0 ? (
        <p className="alert alert-warn" role="status">
          {custItemBlockedRows.length} line(s) are on the customer item block list — check reasons
          before confirming.
        </p>
      ) : null}

      {invalidRows.length > 0 ? (
        <p className="alert alert-error">
          {invalidRows.length} row(s) need a system item code or type IGNORE.
        </p>
      ) : null}

      <div className="dem-po-review__actions">
        <button
          type="button"
          className="btn-primary"
          disabled={saving || rematching || !header.sys_customer_code?.trim() || invalidRows.length > 0}
          onClick={onConfirm}
        >
          {saving ? 'Saving…' : 'Confirm & save to Pending'}
        </button>
      </div>
        </div>

        {stdOpen ? (
          <StandardSalesLinesPanel
            lines={stdLines}
            loading={stdLoading}
            customerCode={header.sys_customer_code?.trim() ?? ''}
            onHide={() => setStdOpen(false)}
          />
        ) : (
          <div className="dem-po-review__std-rail dem-po-review__std-rail--overlay">
            <button
              type="button"
              className="dem-po-review__std-reopen"
              onClick={() => setStdOpen(true)}
              title="Show standard sales lines"
              aria-expanded={false}
            >
              <span className="dem-po-review__std-reopen-icon" aria-hidden>
                ‹
              </span>
              <span className="dem-po-review__std-reopen-label">Std lines</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
